import { withStyles } from '@mui/styles';
import React, { useState } from 'react';
import { FocusOn } from 'react-focus-on';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { getCurrentConference } from '../../base/conference/functions';
import { IC_ROLES } from '../../base/conference/icRoles';
import { getLocalParticipant, getRemoteParticipants, isParticipantModerator } from '../../base/participants/functions';
import { isElementInTheViewport } from '../../base/ui/functions.web';
import { getParticipant } from './functions';
const styles = (theme) => {
    return {
        assisteesPanel: {
            maxWidth: '600px',
            height: 'auto',
            backgroundColor: theme.palette.ui01,
            position: 'absolute',
            bottom: 'calc(50% - 140px)',
            left: 'calc(50% - 300px)',
            padding: theme.spacing(3),
            borderRadius: '10px'
        },
        closeButton: {
            position: 'absolute',
            top: '5px',
            right: '15px',
            fontSize: '1.5rem',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none'
        },
        headline: {
            fontSize: '1.5rem',
            color: theme.palette.text01,
            marginBottom: theme.spacing(3),
            marginTop: 0
        },
        description: {
            fontSize: '1rem',
            marginBottom: theme.spacing(3)
        },
        list: {
            listStyleType: 'none',
            paddingLeft: '0px'
        },
        participantEntry: {
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '1rem'
        },
        participantName: {
            flexGrow: 1,
            fontSize: '1.2rem',
            color: theme.palette.text01,
            fontWeight: 500,
            alignSelf: 'center'
        },
        participantButton: {
            fontSize: '1.2rem',
            cursor: 'pointer',
            alignSelf: 'center',
            borderRadius: '30px',
            padding: '0.5rem 1rem !important'
        },
        divider: {
            borderStyle: 'dashed',
            marginBottom: '1.2rem',
            marginTop: '1.2rem'
        },
        focusLock: {
            zIndex: 1
        }
    };
};
const AssisteesPanel = ({ classes, _assistancePartner, _assistees, _conference, _localParticipant, _participant, _visible, _isLocalParticipantModerator }) => {
    const { t } = useTranslation();
    const [localClose, setLocalClose] = useState(false);
    const _renderAssistees = (data, i) => {
        const _onClickOfferAssistance = () => {
            _conference?.addLocalICRole(IC_ROLES.ASSISTANT, data.participantId);
        };
        return (React.createElement("li", { key: i },
            React.createElement("div", { className: classes.participantEntry },
                React.createElement("div", { className: classes.participantName }, data.name),
                React.createElement("div", null,
                    React.createElement("button", { className: [classes.participantButton, 'primary'].join(' '), 
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick: _onClickOfferAssistance }, t('assisteesPanel.buttonAssist'))))));
    };
    const _onClickClose = () => {
        setLocalClose(true);
        _assistancePartner?.forEach(partner => {
            _conference?.removeICRole(partner.id, IC_ROLES.ASSISTED, _localParticipant?.id);
        });
    };
    let isAssistancePartner = false;
    _assistancePartner.forEach(partner => {
        if (_conference?.checkMemberHasRole(partner.id, IC_ROLES.ASSISTED, _localParticipant?.id)) {
            isAssistancePartner = true;
        }
    });
    const showDialog = _visible && !localClose && isAssistancePartner;
    return showDialog ? (React.createElement(FocusOn, { className: classes.focusLock, returnFocus: 
        // If we return the focus to an element outside the viewport the page will scroll to
        // this element which in our case is undesirable and the element is outside of the
        // viewport on purpose (to be hidden). For example if we return the focus to the toolbox
        // when it is hidden the whole page will move up in order to show the toolbox. This is
        // usually followed up with displaying the toolbox (because now it is on focus) but
        // because of the animation the whole scenario looks like jumping large video.
        isElementInTheViewport },
        React.createElement("div", { "aria-modal": 'true', className: classes.assisteesPanel, role: 'dialog' },
            React.createElement("button", { className: classes.closeButton, 
                // eslint-disable-next-line react/jsx-no-bind
                onClick: _onClickClose, role: 'button' }, "x"),
            React.createElement("h1", { className: classes.headline, id: 'assisteesHeadline' }, t('assisteesPanel.headline')),
            React.createElement("p", { className: classes.description }, t('assisteesPanel.desc1')),
            React.createElement("p", { className: classes.description }, t('assisteesPanel.desc2')),
            React.createElement("hr", { className: classes.divider }),
            React.createElement("ul", { className: classes.list }, _assistees.map((data, i) => _renderAssistees(data, i)))))) : null;
};
const mapStateToProps = (state) => {
    const conference = getCurrentConference(state);
    const remoteParticipants = getRemoteParticipants(state);
    const localParticipant = getLocalParticipant(state);
    const participants = Array.from(remoteParticipants?.values() ?? []);
    participants.push(localParticipant);
    const alreadyAssisted = participants
        .filter(participant => conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTANT))
        .reduce((acc, participant) => {
        conference?.getMemberICRoles(participant.id).forEach((role) => {
            if (role.name === IC_ROLES.ASSISTANT) {
                if (role.partner !== null) {
                    acc.push(role.partner);
                }
            }
        });
        return acc;
    }, []);
    // Get list of users seeking assistances
    const nonAssistet = participants
        .filter((participant) => participant.id !== localParticipant?.id)
        .filter((participant) => !conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTANT))
        .filter((participant) => conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTED))
        .filter((participant) => !alreadyAssisted.includes(participant.id))
        .map((participant) => {
        return {
            id: participant.id,
            name: participant.name,
            participantId: participant.id
        };
    });
    const assistancePartner = participants.filter((participant) => conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTED, localParticipant?.id));
    return {
        _assistancePartner: assistancePartner,
        _assistees: nonAssistet ?? [],
        _conference: conference,
        _isLocalParticipantModerator: isParticipantModerator(localParticipant),
        _localParticipant: localParticipant,
        _participant: getParticipant(state),
        _remoteParticipants: remoteParticipants,
        _visible: nonAssistet.length > 0
    };
};
export default withStyles(styles)(connect(mapStateToProps)(AssisteesPanel));
